<template>
	<!-- Title detail link with path -->
	<a v-if="title && computedHref" :href="computedHref" @click.exact="$event => navigateTitle($event)" v-bind="$attrs">
		<slot />
	</a>

	<!-- Title detail without path - opens modal -->
	<div
		v-else-if="title && !computedHref"
		role="button"
		tabindex="0"
		class="poster-link"
		@click.prevent="() => openTitleModal(title)"
	>
		<slot />
	</div>

	<a v-else-if="needsCrossDomainNavigation" :href="computedHref" v-bind="$attrs">
		<slot />
	</a>

	<NuxtLink v-else :to="computedTo" v-bind="$attrs" @click="$emit('click')">
		<slot />
	</NuxtLink>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';

import { buildUrlForDomain, Domain } from '@/routing/helpers/subdomain-helper';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { captureMessageForSentry } from '@/libraries/sentry';
import { openTitleDetailModal } from '@/helpers/title-modal-helper';

import type { ObjectType } from '@/@types/graphql-types';
import { WebLocale } from '@/enums/web-locale';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';
import type { RouteLocation } from 'vue-router';

interface MinimumLinkTitle {
	objectId: number;
	objectType: ObjectType;
	content: {
		title: string;
		originalReleaseYear?: number | null;
		fullPath?: string;
	};
}

const props = defineProps<{
	to?: RouteLocation;
	title?: MinimumLinkTitle;
}>();

const emit = defineEmits(['clickOut', 'click']);

const router = useRouter();
const { webLocale } = storeToRefs(useLanguageStore());
const { activeDomain } = storeToRefs(useRoutingStore());

/**
 * Auto-detect the target domain based on route meta subdomain field
 */
const targetDomain = computed((): Domain => {
	if (props.to == null) return Domain.MAIN;

	// Try to resolve the route to get its meta information
	try {
		const resolved = router.resolve(props.to);
		const routeMeta = resolved.meta;

		// Check if route meta has subdomain field
		if (routeMeta?.subdomain) {
			return routeMeta.subdomain as Domain;
		}
	} catch (error) {
		// If route resolution fails, fall through to default logic
		captureMessageForSentry(
			'SmartLink: Could not resolve route for meta detection',
			{
				where: '[SmartLink.vue] targetDomain',
				additionalData: { route: props.to, fullPath: props.to?.fullPath, error },
			},
			'warning'
		);
	}

	// Default to main domain if no subdomain specified in meta
	return Domain.MAIN;
});

/**
 * Determine if we need cross-domain navigation
 */
const needsCrossDomainNavigation = computed((): boolean => {
	return activeDomain.value !== targetDomain.value;
});

/**
 * Compute the 'to' prop for router-link (same-domain navigation)
 */
const computedTo = computed(() => {
	// Return undefined as we're making a cross-domain navigation via the href attribute
	if (needsCrossDomainNavigation.value) return undefined;

	const route = props.to;
	if (!route) return undefined;

	// Apply locale prefixing if the route has a valid path
	if (!route.path || typeof route.path !== 'string' || !route.path.trim()) return route;

	return {
		...route,
		path: withLocalePrefix(route.path, route.name?.toString()),
	};
});

/**
 * Generate href URL for both same-domain and cross-domain navigation
 * This ensures SEO-friendly href attributes are always present
 */
const computedHref = computed((): string | undefined => {
	if (props.title) {
		const fullPath = props.title?.content?.fullPath;
		if (!fullPath) return undefined;

		return buildUrlForDomain(fullPath, Domain.MAIN);
	}

	const route = props.to;
	if (!route) {
		captureMessageForSentry(
			'SmartLink: No route provided for navigation',
			{
				currentDomain: activeDomain.value,
				targetDomain: targetDomain.value,
				where: '[SmartLink.vue] computedHref',
				additionalData: props,
			},
			'warning'
		);
		return '#';
	}

	// Resolve the route to a path using the router
	let navigationPath: string;

	// Check for valid path (non-empty string)
	if (route.path && typeof route.path === 'string' && route.path.trim()) {
		// Use the path directly
		navigationPath = route.path;
	} else if (route.name && typeof route.name === 'string' && route.name.trim()) {
		// Resolve the named route to a path using the router
		try {
			const resolved = router.resolve(route);
			navigationPath = resolved.href;
		} catch (error) {
			captureMessageForSentry(
				'SmartLink: Failed to resolve route for navigation',
				{
					error: error as Error,
					currentDomain: activeDomain.value,
					targetDomain: targetDomain.value,
					where: '[SmartLink.vue] computedHref',
					additionalData: { route: route, fullPath: route.fullPath },
				},
				'warning'
			);

			return '#';
		}
	} else {
		// Try to resolve the entire route object as a fallback (handles cases with only params, etc.)
		try {
			const resolved = router.resolve(route);
			if (resolved.href && resolved.href.trim()) {
				navigationPath = resolved.href;
			} else {
				// Only log as error if we truly can't resolve anything useful
				captureMessageForSentry(
					'SmartLink: Route has no valid name or path for navigation',
					{
						currentDomain: activeDomain.value,
						targetDomain: targetDomain.value,
						where: '[SmartLink.vue] computedHref',
						additionalData: { route, originalTo: props.to },
					},
					'warning'
				);
				return '#';
			}
		} catch (error) {
			captureMessageForSentry(
				'SmartLink: Cannot resolve route object for navigation',
				{
					currentDomain: activeDomain.value,
					targetDomain: targetDomain.value,
					where: '[SmartLink.vue] computedHref',
					additionalData: { route, error, originalTo: props.to },
				},
				'warning'
			);
			return '#';
		}
	}

	// Apply locale prefixing
	navigationPath = withLocalePrefix(navigationPath, route.name?.toString());

	// For cross-domain navigation, build full URL with different domain
	if (needsCrossDomainNavigation.value) {
		// BR_SUBDOMAIN: home is host-agnostic on the *current* domain (withLocalePrefix
		// deliberately returns '/' for it, dropping any locale), but guides/sports have no
		// dedicated BR host of their own - so when crossing back to the main domain, a BR-locale
		// user still needs to land on BR_HOST rather than www. Path stays '/' (app.home's only
		// path - prefixing it with /br would resolve to the popular-list route instead), so the
		// host override is threaded explicitly via forceBrHost rather than via the path.
		const isBrHomeCrossover =
			route.name === 'app.home' && targetDomain.value === Domain.MAIN && webLocale.value === WebLocale.br;
		return buildUrlForDomain(navigationPath, targetDomain.value, { forceBrHost: isBrHomeCrossover });
	}

	// For same-domain navigation, return the path (which will be used by router-link)
	return navigationPath;
});

/**
 * Add locale prefix to a path (except for special routes like home)
 */
function withLocalePrefix(path: string, routeName?: string): string {
	if (!path) return path;

	// Special case: home route should always go to root without locale prefix
	if (routeName === 'app.home') {
		return '/';
	}

	// If path already has locale prefix, return as-is
	if (/^\/[a-z]{2,}(\/|$)/.test(path)) return path;

	// Get current locale code
	const locale = webLocale.value;
	if (!locale) return path;

	// Handle root path
	if (path === '' || path === '/') {
		return `/${locale}`;
	}

	// Handle path segments without leading slash
	if (!path.startsWith('/')) {
		return `/${locale}/${path}`;
	}

	// Handle paths with leading slash
	return `/${locale}/${path}`.replace(/\/+/g, '/');
}

/**
 * Navigate for title detail links
 */
async function navigateTitle(evt: MouseEvent) {
	evt.preventDefault();
	emit('click');

	// INP case: artificially give the JS thread time to breathe by adding a re-render.
	await new Promise(resolve => setTimeout(resolve, 0));

	const fullPath = props.title?.content?.fullPath;

	// Title detail links ALWAYS go to main domain
	// Use window.location for cross-domain navigation when not on main domain
	if (activeDomain.value !== Domain.MAIN && fullPath) {
		// The computedHref will have already built the correct cross-domain URL to main domain
		const href = computedHref.value;
		if (href) {
			// This is a cross-domain URL, navigate with window.location
			window.location.href = href;
			return;
		}
	}

	// Same-domain navigation, use router
	router.push(fullPath ?? '').catch(error => {
		if (
			!isNavigationFailure(error, NavigationFailureType.duplicated) &&
			!isNavigationFailure(error, NavigationFailureType.cancelled)
		) {
			throw error;
		}
	});
}

/**
 * Open title modal when no fullPath is provided
 */
function openTitleModal(title: MinimumLinkTitle | undefined) {
	emit('click');

	if (title) {
		openTitleDetailModal(title, 'SmartLink');
	}
}
</script>

<style scoped lang="scss">
// From TitleDetailLink - for modal trigger when no fullPath
.poster-link {
	all: unset;
	width: fit-content;
	display: block;
	cursor: pointer;
}
</style>
